import React from "react";
import { Link } from "react-router-dom";
import {
  TableRow,
  TableCell,
  Box,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Edit,
  Audiotrack,
  Book,
  Category,
  SubdirectoryArrowRight,
  Collections,
  EmojiEvents,
  MenuBook,
} from "@mui/icons-material";

const getParentType = (session) => {
  if (session.category_id) return "Category";
  if (session.subcategory_id) {
    return session.subcategory_is_book  ? "AudioBook" : "Subcategory";
  }
  if (session.series_id) return "Series";
  if (session.challenge_id) return "Challenge";
  return "None";
};

const getParentIcon = (session) => {
  if (session.category_id) return <Category />;
  if (session.subcategory_id) {
    return session.subcategory_is_book  ? <MenuBook /> : <SubdirectoryArrowRight />;
  }
  if (session.series_id) return <Collections />;
  if (session.challenge_id) return <EmojiEvents />;
  return null;
};

const SessionTableRow = ({ session, parentName }) => {
  return (
    <TableRow
      sx={{
        '&:hover': { backgroundColor: '#f5f5f5' },
        '& .MuiTableCell-root': {
          padding: '4px 8px',
          fontSize: '0.8rem'
        }
      }}
    >
      <TableCell sx={{ minWidth: 180, width: '24%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {session.subcategory_is_book  && (
            <Tooltip title={session.session_is_chapter  ? "Chapter" : "Audio Track"}>
              {session.session_is_chapter  ? (
                <Book sx={{ fontSize: 14, color: 'text.secondary' }} />
              ) : (
                <Audiotrack sx={{ fontSize: 14, color: 'text.secondary' }} />
              )}
            </Tooltip>
          )}
          <Typography variant="body2" sx={{ wordBreak: 'break-word', fontSize: '0.8rem', lineHeight: 1.2 }}>
            {session.session_name}
          </Typography>
        </Box>
      </TableCell>
      <TableCell sx={{ minWidth: 120, width: '16%' }}>
        {getParentType(session) !== "None" ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {React.cloneElement(getParentIcon(session), { sx: { fontSize: 14 } })}
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
              {parentName}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>None</Typography>
        )}
      </TableCell>
      <TableCell align="center" sx={{ minWidth: 80, width: '10%' }}>
        <Typography
          variant="body2"
          sx={{
            color: session.session_is_active  ? 'success.main' : 'error.main',
            fontWeight: 'bold',
            fontSize: '0.75rem'
          }}
        >
          {session.session_is_active  ? 'Active' : 'Inactive'}
        </Typography>
      </TableCell>
      <TableCell align="center" sx={{ minWidth: 80, width: '10%', fontSize: '0.75rem' }}>
        {session.session_is_recommended  ? 'Yes' : 'No'}
      </TableCell>
      <TableCell align="center" sx={{ minWidth: 80, width: '10%', fontSize: '0.75rem' }}>
        {session.session_is_duplicate  ? 'Yes' : 'No'}
      </TableCell>
      <TableCell align="center" sx={{ minWidth: 80, width: '10%', fontSize: '0.75rem' }}>
        {session.session_is_free ? 'Free' : 'Premium'}
      </TableCell>
      <TableCell align="center" sx={{ minWidth: 60, width: '10%' }}>
        {session.author_image && (
          <Tooltip title={session.author_name}>
            <Box
              component="img"
              src={session.author_image}
              alt={session.author_name}
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                objectFit: 'cover',
                border: '1px solid',
                borderColor: 'divider'
              }}
            />
          </Tooltip>
        )}
      </TableCell>
      <TableCell align="right" sx={{ minWidth: 80, width: '10%' }}>
        <Tooltip title="Edit">
          <IconButton
            component={Link}
            to={`/sessions/edit/${session.session_id}`}
            color="info"
            size="small"
            sx={{
              '&:hover': { backgroundColor: 'action.hover' },
              padding: '4px'
            }}
          >
            <Edit sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

export default SessionTableRow;
